import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Button, Dialog, TextField } from '@material-ui/core';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import numberWithCommas from '../../utils/numberWithCommas';
import { sendOffer } from '../../redux/actions/product';
import {
  SEND_OFFER_REQUESTED,
  SEND_OFFER_SUCCESSFULL,
  SEND_OFFER_ERRORED
} from '../../redux/actions/types';
import AdornedButton from '../../commons/AdornedButton';
import Analytics from '../../services/Analytics';
import RTVDialog from '../../commons/dialogs/RTVDialog';
import { withRouter } from 'react-router';
import { expressToggle } from '../../redux/actions/app';

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(4),
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '& > *': {
      margin: theme.spacing(1)
    }
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    '& > button': {
      margin: theme.spacing(1)
    }
  },
  inputs: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  field: {
    margin: theme.spacing(1),
    width: '100px',
    '& > p': {
      textAlign: 'center'
    }
  },
  title: {
    fontSize: '1.2rem',
    textAlign: 'center',
    fontWeight: 600
  },
  productTitle: {
    fontWeight: 500
  },
  originalPrice: {
    fontWeight: 600,
    height: '34px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end'
  },
  alreadyOffer: {
    width: '100%',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '1.2rem'
  }
}));

const errorDictionary = {
  'Selling price cant be higher than original price':
    'El precio de venta no puede ser mayor al precio original',
  'Price must be int': 'El precio debe ser entero',
  'Price must be higher than zero': 'El precio debe ser mayor a cero',
  'Invalid status for product': 'Estado inválido para el producto',
  "Product can't have pending or live offers":
    'El producto no puede estar pendiente o poseer ofertas activas',
  'Stock must be int': 'El stock debe ser entero',
  'Stock must be higher than zero': 'El stock debe ser mayor a cero',
  'Only Showrooms can manage stock':
    'Solo los Showrooms pueden manejar el stock',
  'Offer is lower than a previous offer': 'Tu oferta es demasiado baja',
  'The offer is lower than the min required': 'Tu oferta es demasiado baja',
  'An offer is already live on the product': 'El producto ya está en oferta',
  'User has already offered on this product':
    'Ya has ofertado en este producto',
  "Showrooms don't accept offers": 'La vendedora no acepta ofertas',
  'The offer has to be lower than the selling price':
    'Tu oferta tiene que ser menor al precio de venta',
  'Product is not live': 'El producto ya está vendido',
  "Can't offer on your own product": 'No puedes ofertar en tu propio producto'
};

const OfferButton = ({
  children,
  productStatus,
  productError,
  product,
  user,
  // eslint-disable-next-line no-shadow
  sendOffer,
  ...props
}) => {
  // We need to know if we should show the offer button to the usser
  // or display that they already placed an offer.
  const [alreadyOffer, setAlreadyOffer] = React.useState(false);
  const [currentOffer, setCurrentOffer] = React.useState('');
  const [errorOnSubmit, setErrorOnSubmit] = React.useState(null);
  const [sendingOffer, setSendingOffer] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [success, setOpenSuccess] = React.useState(false);
  const [openOffer, setOpenOffer] = React.useState(false);

  const [openOnPromotion, setOpenOnPromotion] = React.useState(false);
  const productOnPromotion = product.user.promotion !== null;

  const classes = useStyles(props);

  React.useEffect(() => {
    if (user && user.id) {
      if (product.offers.find(offer => offer.user.id === user.id)) {
        setAlreadyOffer(true);
      }
    }
  }, [product.offers, user]);

  // TODO: The legend says different errors will be handled by frontEnd in a given time.
  React.useEffect(() => {
    if (productStatus === SEND_OFFER_REQUESTED) {
      setSendingOffer(true);
    }
    if (productStatus === SEND_OFFER_SUCCESSFULL) {
      Analytics.offerSent(
        product,
        currentOffer.split('$').join('').split('.').join('')
      );
      setSendingOffer(false);
      setOpenOffer(false);
      setOpenSuccess(true);
      setAlreadyOffer(true);
    }
    if (productStatus === SEND_OFFER_ERRORED) {
      setSendingOffer(false);
      if (productError.data) {
        setError(
          errorDictionary[productError.data.message] ||
            'Algo salió mal. Vuelve a intentarlo.'
        );
      } else {
        setError('Algo salió mal. Vuelve a intentarlo.');
      }
    }
  }, [productStatus, productError, currentOffer, product]);

  const onChange = ({ target: { value } }) => {
    // eslint-disable-next-line no-param-reassign
    value = value.split('.').join('');
    // eslint-disable-next-line no-param-reassign
    value = value.split('$').join('');
    if (value.length > 1 && !value.match(/^\d+$/g)) {
      return;
    }

    if (value === '') {
      setCurrentOffer('');
      return;
    }

    setCurrentOffer(`$${numberWithCommas(value)}`);
    if (errorOnSubmit) {
      setErrorOnSubmit(null);
    }
  };

  const send = () => {
    const offer = currentOffer.split('$').join('').split('.').join('');
    const offerValue = Number(offer);
    let minValue = Number((product.final_price * 0.6).toFixed());
    if (product.offers && product.offers[0] && product.offers[0].value) {
      minValue = product.offers[0].value;
    }
    if (offerValue < minValue) {
      setErrorOnSubmit(
        `La oferta mínima para este producto es de $${numberWithCommas(
          minValue
        )}`
      );
      return;
    }
    sendOffer(product.id, offer);
  };

  const handleOpen = () => {
    if (!user) {
      props.expressToggle(true, {
        entity: 'Product',
        identifier: product.id
      });
      return;
    }

    if (productOnPromotion) {
      setOpenOnPromotion(true);
      return;
    }

    setOpenOffer(true);
  };

  return (
    <>
      <RTVDialog
        open={openOnPromotion}
        onClose={() => setOpenOnPromotion(false)}
        title="Oops :("
        message="El producto está en promo por lo que no podés ofertar por el momento"
      />
      <Dialog open={success} onClose={() => setOpenSuccess(false)}>
        <div className={classes.content}>
          <Typography className={classes.title}>
            Oferta pendiente de
            <br />
            respuesta de la vendedora
          </Typography>
          <Typography>{product.title}</Typography>
          <Typography>{currentOffer}</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenSuccess(false)}
          >
            OK
          </Button>
        </div>
      </Dialog>
      <Dialog open={openOffer} onClose={() => setOpenOffer(false)}>
        <div className={classes.content}>
          {error === null && (
            <>
              <Typography className={classes.title} color="primary">
                Estás ofertando en:
              </Typography>
              <Typography className={classes.productTitle}>
                {product.title}
              </Typography>
            </>
          )}
          {error !== null && (
            <Typography className={classes.title} color="primary">
              {error}
            </Typography>
          )}
          <div className={classes.inputs}>
            <div className={classes.field}>
              <Typography>Precio actual</Typography>
              <Typography className={classes.originalPrice}>
                ${numberWithCommas(product.selling_price)}
              </Typography>
            </div>
            <div className={classes.field}>
              <Typography>Oferta</Typography>
              <TextField
                inputProps={{
                  style: {
                    textAlign: 'center',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }
                }}
                value={currentOffer}
                onChange={onChange}
              />
            </div>
          </div>
          <Typography style={{ fontWeight: 300, color: '#707070' }}>
            La vendedora tiene 48hs para responderte!
          </Typography>
          <div className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOpenOffer(false)}
              disabled={sendingOffer}
            >
              Cancelar
            </Button>
            <AdornedButton
              variant="contained"
              color="primary"
              onClick={send}
              loading={sendingOffer}
              disabled={sendingOffer || currentOffer === ''}
            >
              OFERTAR
            </AdornedButton>
          </div>
          {errorOnSubmit && (
            <Typography color="primary">{errorOnSubmit}</Typography>
          )}
        </div>
      </Dialog>
      {alreadyOffer ? (
        <Typography className={classes.alreadyOffer} color="primary">
          Ya ofertaste!
        </Typography>
      ) : (
        React.cloneElement(children, { onClick: handleOpen })
      )}
    </>
  );
};

const mapStateToProps = state => ({
  productStatus: state.product.status,
  productError: state.product.error,
  user: state.auth.user
});

const mapDispatchToProps = dispatch => ({
  sendOffer: (product, offer) => dispatch(sendOffer(product, offer)),
  push: path => dispatch(push(path)),
  expressToggle: (toggle, fromAction) =>
    dispatch(expressToggle(toggle, fromAction))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(OfferButton));
